import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { CheckCircle, XCircle, Clock, AlertCircle, ChevronRight, X, History } from 'lucide-react';
import { auditApi } from '../api/client';
import type { AuditEntry } from '../types';

// Loading skeleton
function TimelineSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map(i => (
        <div key={i} className="relative pl-14 animate-pulse">
          <div className="absolute left-0 top-1 w-8 h-8 bg-slate-700 rounded-full"></div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
            <div className="h-4 w-48 bg-slate-700 rounded mb-2"></div>
            <div className="h-3 w-32 bg-slate-700/50 rounded mb-3"></div>
            <div className="h-3 w-24 bg-slate-700/50 rounded"></div>
          </div>
        </div>
      ))}
    </div>
  );
}

export function AuditTimeline() {
  const [selectedEntry, setSelectedEntry] = useState<AuditEntry | null>(null);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 24 * 60 * 60 * 1000),
    end: new Date(),
  });

  const { data: entries, isLoading } = useQuery({
    queryKey: ['audit', dateRange],
    queryFn: () =>
      auditApi.list({
        startDate: dateRange.start,
        endDate: dateRange.end,
        limit: 100,
      }),
    refetchInterval: 10000,
  });

  const getResultIcon = (result: AuditEntry['result']) => {
    switch (result) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-success-400" />;
      case 'failure':
        return <XCircle className="w-5 h-5 text-danger-400" />;
      case 'denied':
        return <AlertCircle className="w-5 h-5 text-warning-400" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getResultColor = (result: AuditEntry['result']) => {
    switch (result) {
      case 'success':
        return 'border-l-success-500 bg-success-500/5';
      case 'failure':
        return 'border-l-danger-500 bg-danger-500/5';
      case 'denied':
        return 'border-l-warning-500 bg-warning-500/5';
      case 'pending':
        return 'border-l-slate-500 bg-slate-500/5';
    }
  };

  const getResultBadge = (result: AuditEntry['result']) => {
    const badges = {
      success: 'badge-success',
      failure: 'badge-danger',
      denied: 'badge-warning',
      pending: 'bg-slate-700 text-slate-300 border-slate-600',
    };
    return badges[result];
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center">
            <History className="w-5 h-5 text-primary-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Audit Timeline</h2>
            <p className="text-sm text-slate-400">Track all agent actions and decisions</p>
          </div>
        </div>

        <div className="flex gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">Start Date</label>
            <input
              type="datetime-local"
              value={format(dateRange.start, "yyyy-MM-dd'T'HH:mm")}
              onChange={e =>
                setDateRange(prev => ({
                  ...prev,
                  start: new Date(e.target.value),
                }))
              }
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-400 mb-2">End Date</label>
            <input
              type="datetime-local"
              value={format(dateRange.end, "yyyy-MM-dd'T'HH:mm")}
              onChange={e =>
                setDateRange(prev => ({
                  ...prev,
                  end: new Date(e.target.value),
                }))
              }
              className="input"
            />
          </div>
        </div>

        {isLoading ? (
          <TimelineSkeleton />
        ) : entries?.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-slate-800/50 rounded-2xl flex items-center justify-center">
              <History className="w-8 h-8 text-slate-600" />
            </div>
            <p className="text-slate-400">No audit entries found</p>
            <p className="text-sm text-slate-500 mt-1">Try adjusting the date range</p>
          </div>
        ) : (
          <div className="relative">
            <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-700" />

            <div className="space-y-4">
              {entries?.map(entry => (
                <div key={entry.id} className="relative pl-14">
                  <div className="absolute left-0 top-2 bg-slate-800 p-1.5 rounded-full border-2 border-slate-700">
                    {getResultIcon(entry.result)}
                  </div>

                  <div
                    onClick={() => setSelectedEntry(entry)}
                    className={`border-l-4 rounded-xl p-4 cursor-pointer hover:shadow-lg transition-all duration-200 bg-slate-800/50 border border-slate-700/50 hover:border-slate-600/50 ${getResultColor(
                      entry.result,
                    )}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-white">
                          {entry.event}: {entry.action.tool}.{entry.action.operation}
                        </p>
                        <p className="text-sm text-slate-400 mt-1">
                          Actor: {entry.actor.id} ({entry.actor.type})
                        </p>
                        {entry.details && (
                          <p className="text-sm text-slate-300 mt-2">{entry.details}</p>
                        )}
                        <p className="text-xs text-slate-500 mt-2">
                          {format(new Date(entry.timestamp), 'PPpp')}
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-500" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedEntry && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="glass rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto mx-4 animate-slide-up">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary-500/20 rounded-xl flex items-center justify-center">
                  {getResultIcon(selectedEntry.result)}
                </div>
                <h3 className="text-lg font-semibold text-white">Audit Entry Details</h3>
              </div>
              <button
                onClick={() => setSelectedEntry(null)}
                className="btn btn-ghost p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Event</p>
                  <p className="text-white">{selectedEntry.event}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Result</p>
                  <div className="flex items-center gap-2">
                    {getResultIcon(selectedEntry.result)}
                    <span className={`badge ${getResultBadge(selectedEntry.result)}`}>
                      {selectedEntry.result}
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-slate-400 mb-1">Action</p>
                <p className="text-white font-mono">
                  {selectedEntry.action.tool}.{selectedEntry.action.operation}
                </p>
              </div>

              {selectedEntry.action.parameters && (
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-2">Parameters</p>
                  <pre className="bg-slate-800/50 rounded-lg p-4 text-sm text-slate-300 font-mono overflow-x-auto border border-slate-700/50">
                    {JSON.stringify(selectedEntry.action.parameters, null, 2)}
                  </pre>
                </div>
              )}

              {selectedEntry.policyDecision && (
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-2">Policy Decision</p>
                  <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/50">
                    <p className="text-sm text-slate-300">
                      <span className="font-medium text-white">Effect:</span>{' '}
                      {selectedEntry.policyDecision.effect}
                    </p>
                    <p className="text-sm text-slate-300 mt-1">
                      <span className="font-medium text-white">Reason:</span>{' '}
                      {selectedEntry.policyDecision.reason}
                    </p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-700/50">
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Session ID</p>
                  <p className="font-mono text-sm text-slate-300">{selectedEntry.sessionId}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-400 mb-1">Timestamp</p>
                  <p className="text-slate-300">{format(new Date(selectedEntry.timestamp), 'PPpp')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
